// Data Access Layer: User Skills
import { supabase } from '../supabase';
import type { 
  UserSkill, 
  UserSkillInsert, 
  UserSkillUpdate, 
  UserSkillWithDetails 
} from '../../types/database';

/**
 * Get current user ID (adapt for your auth provider)
 */
async function getCurrentUserId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  return user?.id || null;
}

/**
 * Fetch all skills for the current user
 */
export async function getUserSkills(): Promise<UserSkillWithDetails[]> {
  const userId = await getCurrentUserId();
  
  if (!userId) {
    return [];
  }

  const { data, error } = await supabase
    .from('user_skills')
    .select(`
      *,
      skill:skills(*)
    `)
    .eq('user_id', userId)
    .order('updated_at', { ascending: false });

  if (error) {
    console.error('Error fetching user skills:', error);
    throw new Error(`Failed to fetch user skills: ${error.message}`);
  }

  return data || [];
}

/**
 * Add or update a user skill
 */
export async function upsertUserSkill(skill: UserSkillInsert): Promise<UserSkill> {
  const userId = await getCurrentUserId();
  
  if (!userId) {
    throw new Error('No authenticated user');
  }

  const { data, error } = await supabase
    .from('user_skills')
    .upsert({
      ...skill,
      user_id: userId,
    })
    .select()
    .single();

  if (error) {
    console.error('Error upserting user skill:', error);
    throw new Error(`Failed to save user skill: ${error.message}`);
  }

  return data;
}

/**
 * Update an existing user skill
 */
export async function updateUserSkill(
  skillId: string,
  updates: UserSkillUpdate
): Promise<UserSkill> {
  const userId = await getCurrentUserId();
  
  if (!userId) {
    throw new Error('No authenticated user');
  }

  const { data, error } = await supabase
    .from('user_skills')
    .update(updates)
    .eq('user_id', userId)
    .eq('skill_id', skillId)
    .select()
    .single();

  if (error) {
    console.error('Error updating user skill:', error);
    throw new Error(`Failed to update user skill: ${error.message}`);
  }

  return data;
}

/**
 * Delete a user skill
 */
export async function deleteUserSkill(skillId: string): Promise<void> {
  const userId = await getCurrentUserId();
  
  if (!userId) {
    throw new Error('No authenticated user');
  }

  const { error } = await supabase
    .from('user_skills')
    .delete()
    .eq('user_id', userId)
    .eq('skill_id', skillId);

  if (error) {
    console.error('Error deleting user skill:', error);
    throw new Error(`Failed to delete user skill: ${error.message}`);
  }
}
